/*eslint-disable*/
import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    MenuItem,
    Select,
    FormControl,
    Grid,
    Box,
    Skeleton,
    CircularProgress,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    selectClasses,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart"; // Import BarChart
import { SelectChangeEvent } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FastAPIClient from "../../client";
import config from "../../config";
import CustomHorizontalBarChart from "./CustomHorizontalBarChart";
import ChartWithTable from "./ChartWithTable";
import StatCard from "../LayoutComponents/StatCard";
import SimplifiedCoverageTable from "./NumericDistributionTable";
import UserCoverage from "./UserCoverage";
import { FiChevronDown } from "react-icons/fi";

import {
    DataGridPremium,
    GridColDef,
    GridToolbar,
} from "@mui/x-data-grid-premium";

import utc from "dayjs/plugin/utc";
import ClientCoverageTable from "./ClientCoverageTable";
import ClientTable from "./ClientTable";
dayjs.extend(utc);

const client = new FastAPIClient(config);

type ClientInteractionStat = {
    user_id: string;
    clients_at_risk: number;
    clients_without_order: number;
    clients_without_interaction: number;
};

interface ClientInteractionTableProps {
    data: ClientInteractionStat[];
    userNameMap: Record<string, string>; // maps user_id to name
}

const ClientInteractionTable: React.FC<ClientInteractionTableProps> = ({
    data,
    userNameMap,
}) => {
    // Build columns dynamically based on users
    const userIds = data.map((d) => d.user_id);
    const columns: GridColDef[] = [
        {
            field: "type",
            headerName: "Type",
            flex: 1,
        },
        // ...userIds.map(
        //   (uid) =>
        //     ({
        //       field: uid,
        //       headerName: userNameMap[uid] || uid,
        //       flex: 1,
        //       type: "number",
        //     } as GridColDef)
        // ),
        {
            field: "Total",
            headerName: "Total",
            flex: 1,
            type: "number",
            headerClassName: "total-column-header",
            cellClassName: "total-column-cell",
        },
    ];

    // Create pivoted rows
    const rows = [
        {
            id: "no_order",
            type: "Sans commande",
            ...Object.fromEntries(
                data.map((d) => [d.user_id, d.clients_without_order])
            ),
            Total: data.reduce((sum, d) => sum + d.clients_without_order, 0),
        },
        {
            id: "no_interaction",
            type: "Sans interaction",
            ...Object.fromEntries(
                data.map((d) => [d.user_id, d.clients_without_interaction])
            ),
            Total: data.reduce(
                (sum, d) => sum + d.clients_without_interaction,
                0
            ),
        },
        {
            id: "at_risk",
            type: "À risque",
            ...Object.fromEntries(
                data.map((d) => [d.user_id, d.clients_at_risk])
            ),
            Total: data.reduce((sum, d) => sum + d.clients_at_risk, 0),
        },
    ];

    const [expanded, setExpanded] = useState<boolean>(true);

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            disableGutters
            elevation={0}
            square
            sx={{
                mb: 1,
                boxShadow: "none",
                border: "none",
                "&::before": {
                    display: "none", // removes the default divider line
                },
            }}
        >
            <AccordionSummary
                expandIcon={<FiChevronDown />}
                sx={{
                    p: 0, // removes all padding
                    m: 0, // removes all margin
                    minHeight: 0,
                    "& .MuiAccordionSummary-content": {
                        m: 0,
                        p: 0,
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                        p: 0, // optional: tighten up the expand icon padding
                        m: 0,
                    },
                }}
            >
                <Typography
                    variant="subtitle2"
                    fontSize="0.875rem"
                    sx={{ fontWeight: "bold" }}
                >
                    Activité clients
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box>
                    <DataGridPremium
                        rows={rows}
                        columns={columns}
                        rowHeight={30}
                        hideFooter
                        disableColumnMenu
                        disableRowSelectionOnClick
                        density="compact"
                        slots={{
                            columnHeaders: () => null,
                        }}
                        sx={{
                            overflow: "hidden",
                            border: "none",
                            "& .MuiDataGrid-root": {
                                border: "none",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                borderBottom: "none",
                            },
                            "& .MuiDataGrid-cell": {
                                fontSize: "0.75rem",
                                padding: "1px",
                                borderBottom: "none",
                                borderTop: "none",
                                textAlign: "left",
                                display: "flex",
                            },
                            "& .positive-change": {
                                color: "#009A0A",
                            },
                            "& .negative-change": {
                                color: "#FF365E",
                            },
                            // Hover to show scrollbar
                            "&:hover": {
                                "&::-webkit-scrollbar": {
                                    opacity: 1, // Make scrollbar visible on hover
                                },
                            },
                            "&::-webkit-scrollbar": {
                                width: "8px", // Width of the scrollbar
                                opacity: 0, // Initially hide the scrollbar
                                transition: "opacity 0.3s", // Smooth transition
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "#f1f1f1", // Light track color
                            },
                            "&::-webkit-scrollbar-thumb": {
                                background: "#888", // Scrollbar thumb color
                                // borderRadius: "4px", // Rounded corners for the scrollbar thumb
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                                background: "#555", // Hover effect for the scrollbar thumb
                            },
                        }}
                    />
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

const formatNumber = (num: number | null): string => {
    if (num == null) return ""; // Handles null case
    if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
    if (Math.abs(num) < 1_000_000 && Math.abs(num) >= 100_000)
        return (num / 1_000).toFixed(0) + "k";
    if (Math.abs(num) < 100_000) return (num / 1_000).toFixed(1) + "k";
    return num.toString();
};

type MonthlyStat = {
    user_id: string;
    first_name: string;
    last_name: string;
    warehouse_name: string;
    year: number;
    month: number;
    monthly_revenue: number;
    monthly_volume: number;
    monthly_margin: number;
    distinct_clients: number;
    team_name: string;
    product_level_1?: string; // 👈 Add this
    client_level_1?: string; // 👈 Add this
};

interface UserMeetingProps {
    selectedUser: string;
    // selectedTeam: string;
    selectedWarehouse: string;
    // timePeriod: string;
    metricKey: "monthly_revenue" | "monthly_volume" | "monthly_margin";
    title: string;
    data: MonthlyStat[];
    startDate: string;
    endDate: string;
}

interface NumericDistributionEntry {
    year: number;
    product_level_1: string | null;
    distinct_clients: number;
    total_clients: number;
    numeric_distribution: number;
}

interface ClientDataEntry {
    label: string;
    value: number;
    change: number;
}

const ClientStats: React.FC<UserMeetingProps> = ({
    selectedUser,
    // selectedTeam,
    selectedWarehouse,
    // timePeriod,
    metricKey,
    title,
    data,
    startDate,
    endDate,
}) => {
    // const [data, setData] = useState<MonthlyStat[] | null>(null);
    // const [selectedUser, setSelectedUser] = useState<string>("all");
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().startOf("week"),
        dayjs().endOf("week"),
    ]);

    const [clientInteractionData, setClientInteractionData] = useState<
        ClientInteractionStat[] | null
    >(null);

    const [numericDistributionData, setNumericDistributionData] = useState<
        NumericDistributionEntry[] | null
    >(null);

    const [clientData, setClientData] = useState<ClientDataEntry[] | null>(
        null
    );

    const [filteredClientData, setFilteredClientData] = useState<
        ClientDataEntry[] | null
    >(null);

    useEffect(() => {
        fetchActivity();
        fetchNumericDistribution();
        fetchClients();
    }, [selectedWarehouse, selectedUser]);

    const fetchActivity = async () => {
        try {
            const response = await client.fetchClientInteractionStats(
                "2025-01-01",
                dayjs().format("YYYY-MM-DD"),
                selectedWarehouse,
                selectedUser
            );
            setClientInteractionData(response.results);
            console.log("couverture", response.results);
        } catch (error) {
            console.error("Error fetching activity:", error);
        }
    };

    const fetchNumericDistribution = async () => {
        try {
            const response = await client.fetchNumericDistribution(
                "2025-01-01",
                dayjs().format("YYYY-MM-DD"),
                selectedWarehouse,
                selectedUser
            );
            setNumericDistributionData(response.results);
        } catch (error) {
            console.error("Error fetching activity:", error);
        }
    };

    const fetchClients = async () => {
        try {
            const response = await client.fetchClients(
                "2025-01-01",
                dayjs().format("YYYY-MM-DD")
            );

            // Filter out any data related to users with last name "Bouvy"
            const bouvyFilteredResults = response.results.filter(
                (item) =>
                    !item.last_name.toLowerCase().includes("bouvy") &&
                    !item.last_name.toLowerCase().includes("bouv") &&
                    !item.last_name.toLowerCase().includes("bouvi")
            );

            let formattedData;

            if (selectedWarehouse === "all" && selectedUser === "all") {
                // Aggregate by warehouse_name
                const aggregated = bouvyFilteredResults.reduce(
                    (acc, curr) => {
                        const key = curr.warehouse_name;
                        if (!acc[key]) {
                            acc[key] = {
                                label: key,
                                value: 0,
                                lastYear: 0,
                            };
                        }
                        acc[key].value += curr.distinct_clients_this_year;
                        acc[key].lastYear += curr.distinct_clients_last_year;
                        return acc;
                    },
                    {} as Record<
                        string,
                        { label: string; value: number; lastYear: number }
                    >
                );

                formattedData = Object.values(
                    aggregated as Record<
                        string,
                        {
                            label: string;
                            value: number;
                            lastYear: number;
                        }
                    >
                ).map((item) => ({
                    label: item.label,
                    value: item.value,
                    change: item.value - item.lastYear,
                }));
            } else if (selectedUser !== "all") {
                // Filter by warehouse and selected user
                const userFilteredResults = bouvyFilteredResults.filter(
                    (item) =>
                        `${item.first_name} ${item.last_name}` === selectedUser
                );

                console.log();

                formattedData = userFilteredResults.map((item) => ({
                    label: `${item.first_name} ${item.last_name}`,
                    value: item.distinct_clients_this_year,
                    change:
                        item.distinct_clients_this_year -
                        item.distinct_clients_last_year,
                }));
            } else {
                // Show individual user stats with first/last name
                const warehouseFilteredResults = bouvyFilteredResults.filter(
                    (item) => item.warehouse_name === selectedWarehouse
                );

                formattedData = warehouseFilteredResults.map((item) => ({
                    label: `${item.first_name} ${item.last_name}`,
                    value: item.distinct_clients_this_year,
                    change:
                        item.distinct_clients_this_year -
                        item.distinct_clients_last_year,
                }));
            }

            setClientData(formattedData);
            setFilteredClientData(formattedData);
        } catch (error) {
            console.error("Error fetching clients:", error);
        }
    };

    const userNameMap = useMemo(() => {
        const map: { [key: string]: string } = {};
        data.forEach((d) => {
            map[d.user_id] = `${d.first_name} ${d.last_name}`;
        });
        return map;
    }, [data]);

    const totalClients = useMemo(() => {
        if (!filteredClientData) return 0;
        return filteredClientData.reduce(
            (sum, client) => sum + (client.value || 0),
            0
        );
    }, [filteredClientData]);

    return (
        <StatCard title={title} value={`${totalClients}`} loading={!data}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                    {clientData && (
                        <ClientTable
                            title={
                                selectedWarehouse !== "all"
                                    ? "Par utilisateur"
                                    : "Par dépôt"
                            }
                            data={clientData}
                        />
                    )}
                </Box>
                <Box mt={2}>
                    {clientInteractionData && (
                        <ClientInteractionTable
                            data={clientInteractionData}
                            userNameMap={userNameMap}
                        />
                    )}
                </Box>

                <Box>
                    {numericDistributionData && (
                        <SimplifiedCoverageTable
                            data={numericDistributionData}
                        />
                    )}
                </Box>

                <ClientCoverageTable
                    selectedUser={selectedUser}
                    selectedWarehouse={selectedWarehouse}
                    selectedTeam={"all"}
                    timePeriod={"last7Days"}
                    startDate={startDate}
                    endDate={endDate}
                />
            </LocalizationProvider>
        </StatCard>
    );
};

export default ClientStats;
